/**
 * Thin client for the /animator REST endpoints.
 *
 * Kept out of the component so the page owns UI state and this owns "talk to the
 * backend": one place that knows the URLs, the request shape, and how to turn a
 * non-2xx response into a thrown Error with a message worth showing a child.
 */

import { getVoiceSocketUrl } from "../utils/AppConfig";

/** A PNG returned as a base64 payload (no data: prefix). */
export interface CharacterResult {
  /** Chroma-keyed to a real transparent background. */
  imageBase64: string;
  /** The model's raw output before keying — the magenta backdrop is still there. */
  rawImageBase64: string;
}

export interface AnimationResult {
  /** Chroma-keyed sprite sheet, ready to slice and play. */
  imageBase64: string;
  /** The raw, un-keyed sheet — useful for judging whether the grid rendered cleanly. */
  rawImageBase64: string;
  cols: number;
  rows: number;
}

export interface SoundResult {
  /** Raw MP3 bytes, base64-encoded (no data: prefix). */
  audioBase64: string;
}

export interface PromptTemplates {
  character: string;
  animation: string;
  sound: string;
}

async function handleResponse<T>(res: Response): Promise<T> {
  // The server sends { error } on failure; fall back to a generic line if the
  // body isn't the shape we expect (e.g. a proxy 502 with an HTML page).
  if (!res.ok) {
    let message = "Something went wrong. Please try again.";
    try {
      const data = await res.json();
      if (data && typeof data.error === "string") message = data.error;
    } catch {
      /* non-JSON error body — keep the generic message */
    }
    throw new Error(message);
  }
  return res.json() as Promise<T>;
}

async function postJson<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(`${getVoiceSocketUrl()}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return handleResponse<T>(res);
}

/** Fetch the current default prompt templates, for the editable text boxes. */
export async function getPromptTemplates(): Promise<PromptTemplates> {
  const res = await fetch(`${getVoiceSocketUrl()}/animator/prompts`);
  return handleResponse<PromptTemplates>(res);
}

/**
 * Generate a character portrait from a description (Nano Banana 2 lite).
 * `promptTemplate` is the page's current (possibly edited) template text;
 * omit to use the server's built-in default.
 */
export function generateCharacter(
  description: string,
  promptTemplate?: string,
): Promise<CharacterResult> {
  return postJson<CharacterResult>("/animator/character", { description, promptTemplate });
}

/**
 * Generate a 6×4 animation sheet from a description, using the character image
 * as a reference so the animation matches the portrait (Nano Banana 2).
 */
export function generateAnimation(
  description: string,
  characterImage: string,
  promptTemplate?: string,
): Promise<AnimationResult> {
  return postJson<AnimationResult>("/animator/animation", {
    description,
    characterImage,
    promptTemplate,
  });
}

/**
 * Generate a short sound effect matching the action description (ElevenLabs).
 * `character` (the step-1 description) is required — the same action sounds
 * different depending on what's making it, and this call has no reference
 * image to carry that context the way generateAnimation does.
 */
export function generateSoundEffect(
  description: string,
  character: string,
  durationSeconds?: number,
  promptTemplate?: string,
): Promise<SoundResult> {
  return postJson<SoundResult>("/animator/sound", {
    description,
    character,
    durationSeconds,
    promptTemplate,
  });
}
